<script lang="ts">
  import { object, string, date, boolean } from 'yup'
  import { onMount } from 'svelte'
  import DatePicker from '../shared/DatePicker.svelte'
  import { preventDefault, scrollToTop } from '@/utils'
  import Portal from "svelte-portal"
  import TimePicker from '../shared/TimePicker.svelte'
  import Huebee from '../shared/Huebee.svelte'
  import Coupon from './Coupon.svelte'
  import axios from 'axios'
  import DirectFileUpload from '../shared/DirectFileUpload.svelte'

  onMount(setDefaults)

  let { initialCoupon } = $props()
  let coupon = $state(JSON.parse(initialCoupon))
  let mode = $state('coupon')
  let saveDisabled = $state(false)
  let saveButtonText = $derived(saveDisabled ? 'Uploading...' : 'Save')
  let barcodeError = $state('')
  let hasBarcodeError = $derived(!!barcodeError && coupon.redemption_type === 'barcode')
  let barcodeAside = $derived.by(() => {
    if (coupon.redemption_type !== 'barcode') {
      return ''
    }
    if (coupon.barcode_format === 'ITF14') {
      return ' (13 or 14 digits)'
    }
    return ''
  })
  let barcodeAcceptsAlpha = $derived(
    coupon.redemption_type === "standard" || !/ITF14/i.test(coupon.barcode_format)
  )

  let couponSchema = object({
    name: string().required('Name is required'),
    single_redemption_only: boolean(),
    has_expiration_date: boolean(),
    expiration_date: date().nullable().transform((value, originalValue) =>
      originalValue === '' ? null : value
    ).when('has_expiration_date', {
      is: true,
      then: (schema) => schema.required('Expiration date is required')
    }),
    expiration_time: string().nullable().when('has_expiration_date', {
      is: true,
      then: (schema) => schema.required('Expiration time is required')
    }),
    color: string().required(),
    offer_header_text: string().required('Header text is required'),
    offer_title: string().required('Offer title is required'),
    offer_description: string().required('Offer description is required'),
    redeem_button_text: string().nullable(),
    fine_print: string().nullable(),
    show_phone_number: boolean(),
    phone_number: string().nullable().when('show_phone_number', {
      is: true,
      then: (schema) => schema.required()
    }),
    show_website_url: boolean(),
    website_url: string().nullable().when('show_website_url', {
      is: true,
      then: (schema) => schema.required()
    }),
    show_facebook_url: boolean(),
    facebook_url: string().nullable().when('show_facebook_url', {
      is: true,
      then: (schema) => schema.required()
    }),
    show_directions_url: boolean(),
    directions_url: string().nullable().when('show_directions_url', {
      is: true,
      then: (schema) => schema.required()
    }),
  })

  let formState = $state({
    mode: 'edit',
    formErrors: null
  })

  let hasFormErrors = $derived(!!formState?.formErrors?.length)

  const isInvalid = (field) => {
    return formState.formErrors?.includes(field)
  }

  const setCouponMode = () => {
    mode = 'coupon'
  }

  const setRedeemMode = () => {
    mode = 'redeem'
  }

  function setDefaults() {
    coupon.offer_image_src = coupon.offer_image_url
    coupon.offer_image = ''
    coupon.color = coupon.color || "#EBB335"
    coupon.single_redemption_only = coupon.single_redemption_only || false
    coupon.has_expiration_date = coupon.has_expiration_date || false
    coupon.redemption_type = coupon.redemption_type || "standard"
    coupon.barcode_format = coupon.barcode_format || "qr"
  }

  const validateForm = async () => {
    try {
      formState.formErrors = []
      await couponSchema.validate(coupon, { abortEarly: false })
      return true
    } catch (err) {
      formState.formErrors = err.errors
      return false
    }
  }

  async function submitForm(evt) {
    evt.preventDefault()
    await validateForm()

    if (hasFormErrors) {
      scrollToTop()
      return
    }

    let params = { ...coupon }

    // these are ignored by the controller,
    // delete so as not to waste bandwidth
    delete params.offer_image_src
    delete params.offer_image_content_type
    delete params.offer_image_filename
    delete params.offer_image_id
    delete params.offer_image_size

    let path = coupon.id ?
      `/coupons/${coupon.id}.json` :
      `/coupons.json`

    axios[coupon.id ? "put" : "post"](path, {
      coupon
    })
      .then(response => {
        return response.data
      })
      .then(submittedOk)
      .catch(err => {
        console.log('Error submitting form', err)
        submittedFail(err)
      })
  }

  function submittedOk(_response) {
    window['App'].busy.start()
    location.href = "/coupons"
  }

  function submittedFail(errors) {
    console.log('submittedFail', errors)
    formState.formErrors = []

    for (var name in errors) {
      formState.formErrors.push(`${humanize(name)} ${errors[name]}`)
    }
    scrollToTop()
  }

  function humanize(str) {
    let s = str.replace(/_/g, " ")
    return s.charAt(0).toUpperCase() + s.slice(1)
  }

  function barcodeRenderError(error) {
    barcodeError = String(error).replace(/bwipp\.[^:]+: /, '')
  }

  function imageChanged(image) {
    coupon.offer_image_src = image
  }

  function uploadChanged(upload) {
    if (upload) {
      coupon.offer_image = upload.signed_id
    } else {
      coupon.offer_image = null
    }
  }

  function uploadError(error) {
    console.log('uploadError', error)
    formState.formErrors = [error]
  }
</script>

<div class="CouponsForm panel panel--padded">
  <Portal target="#cta-portal">
    <button class="btn btn-success btn-rounded" disabled={saveDisabled} onclick={submitForm}>{saveButtonText}</button>
  </Portal>

  <form class="coupons-form" id="coupons-form" onsubmit={preventDefault(validateForm)}>
    <div class="row">
      <div class="col-md-8">

        {#if hasFormErrors}
          <div class="form-errors">
            <div class="form-errors__inner active" role="alert">
              <div class="form-errors__title">Please fix this:</div>
              <div class="form-errors__msg">
                {#each formState.formErrors as error}
                  <div>{ error }</div>
                {/each}
              </div>
            </div>
          </div>
        {/if}

        <div onfocuscapture={setCouponMode} class="collapsable" data-scroll-container=".phone-screen"
             data-scroll-to="#coupon-content">
          <input checked class="collapsable-cb" id="box1" name="collapsable-toggles" type="checkbox">
          <label class="collapsable-heading" for="box1">
            Coupon Settings
          </label>
          <div class="collapsable-content">
            <div class="form-group" class:has-error={isInvalid('name')}>
              <label class="control-label required" for="coupon-name">
                Coupon Name
                <span class="aside">(Customers will not see this)</span>
              </label>
              <input class="form-control" id="coupon-name" placeholder="Ex: July coupon" required type="text"
                     bind:value={coupon.name}
                     onblur={() => coupon.name = String(coupon.name).trim()}>
              {#if isInvalid('name')}
                <div class="help-block">Field is required.</div>
              {/if}
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="control-label" for="single_redemption_only">
                    Only 1 redemption per person?
                  </label>
                  <select class="form-control" id="single_redemption_only" bind:value={coupon.single_redemption_only}>
                    <option value={false}>No</option>
                    <option value={true}>Yes</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="control-label" for="has_expiration_date">
                    Apply an expiration date?
                  </label>
                  <select class="form-control" id="has_expiration_date" bind:value={coupon.has_expiration_date}>
                    <option value={false}>No</option>
                    <option value={true}>Yes</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row" hidden="{!coupon.has_expiration_date}">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="control-label required" for="expiration_date">
                    Expiration date OR Days till expiration
                  </label>

                  <DatePicker
                    id="expiration_date"
                    bind:value={coupon.expiration_date}
                    format="m/d/Y"
                    editable
                    required={coupon.has_expiration_date}
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <div class="flex-row">
                    <div class="cell-0">
                      <div class="empty-label"></div>
                      <div class="big-at-sign"></div>
                    </div>
                    <div class="cell-1">
                      <div class="empty-label"></div>
                      <TimePicker
                        id="expiration_time"
                        bind:value={coupon.expiration_time}
                        format="h:i K"
                        editable
                        required={coupon.has_expiration_date}
                        placeholder="Ex: 12:00pm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group" style="position: relative;">
                  <label class="control-label required" for="color">
                    Coupon colors
                  </label>
                  <Huebee
                    id="color"
                    required={true}
                    bind:value={coupon.color}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr>
        <div onfocuscapture={setCouponMode} class="collapsable" data-scroll-container=".phone-screen"
             data-scroll-to="#coupon-content">
          <input checked class="collapsable-cb" id="box2" name="collapsable-toggles" type="checkbox">
          <label class="collapsable-heading" for="box2">
            Content
          </label>
          <div class="collapsable-content">
            <div class="form-group" class:has-erro={isInvalid('offer_header_text')}>
              <label class="control-label required" for="offer_header_text">
                Header text
              </label>
              <input class="form-control" id="offer_header_text" placeholder="Ex: July BOGO" required type="text"
                     bind:value={coupon.offer_header_text}
                     onblur={() => coupon.offer_header_text = String(coupon.offer_header_text).trim()}>
              {#if isInvalid('offer_header_text')}
                <div class="help-block">Field is required.</div>
              {/if}
            </div>

            <div class="form-group">
              <label class="control-label" for="offer_image_id">
                Offer image
              </label>
              <DirectFileUpload
                {imageChanged}
                {uploadChanged}
                {uploadError}
                />
            </div>

            <div class="form-group" class:has-error={isInvalid('offer_title')}>
              <label class="control-label required" for="offer_title">
                Offer title
              </label>
              <input class="form-control" id="offer_title" placeholder="Ex: July BOGO" required type="text"
                     bind:value={coupon.offer_title}
                     onblur={() => coupon.offer_title = String(coupon.offer_title).trim()}>
              {#if isInvalid('offer_title')}
                <div class="help-block">Field is required.</div>
              {/if}
            </div>

            <div class="form-group" class:has-error={isInvalid('offer_description')}>
              <label class="control-label required" for="offer_description">
                Offer description
              </label>
              <textarea class="form-control" id="offer_description" placeholder="Ex: July BOGO" required rows="5"
                        bind:value={coupon.offer_description}
                        onblur={() => coupon.offer_description = String(coupon.offer_description).trim()}></textarea>
              {#if isInvalid('offer_description')}
                <div class="help-block">Field is required.</div>
              {/if}
            </div>

            <div class="form-group">
              <label class="control-label" for="redeem_button_text">
                Redeem button text
              </label>
              <input class="form-control" id="redeem_button_text" placeholder="Ex: Redeem Now!" type="text"
                     bind:value={coupon.redeem_button_text}
                     onblur={() => coupon.redeem_button_text = String(coupon.redeem_button_text).trim()}>
            </div>

            <div class="form-group">
              <label class="control-label" for="fine_print">
                Disclaimers / Fine print
              </label>
              <textarea class="form-control" id="fine_print"
                        placeholder="Ex: May not be combined with any other offers"
                        rows="5" bind:value={coupon.fine_print}
                        onblur={() => coupon.fine_print = String(coupon.fine_print).trim()}
                        data-scroll-container=".phone-screen" data-scroll-to="#coupon-fine-print"></textarea>
            </div>

          </div>

          <hr>
          <div onfocuscapture={setCouponMode} class="collapsable" data-scroll-container=".phone-screen"
               data-scroll-to="#coupon-links">
            <input checked class="collapsable-cb" id="box3" name="collapsable-toggles" type="checkbox">
            <label class="collapsable-heading" for="box3">
              Links
            </label>

            <div class="collapsable-content">
              <div class="row">
                <div class="col-md-4">
                  <label class="switch-wrapper">
                    Show phone number?
                    <label class="switch">
                      <input class="switch-checkbox" type="checkbox" bind:checked={coupon.show_phone_number}>
                      <span></span>
                    </label>
                  </label>
                </div>
                <div class="col-md-8">
                  <div class="form-group" class:has-error={isInvalid('phone_number')}>
                    <label class="control-label" for="phone_number" class:required={coupon.show_phone_number}>
                      Phone number
                    </label>
                    <input class="form-control" id="phone_number" type="text" disabled={!coupon.show_phone_number}
                           bind:value={coupon.phone_number}
                           onblur={() => coupon.phone_number = String(coupon.phone_number).trim()}
                           placeholder="Ex: 555-333-4444">
                    {#if isInvalid('phone_number')}
                      <div class="help-block">Field is required.</div>
                    {/if}
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <label class="switch-wrapper">
                    Show website link?
                    <label class="switch">
                      <input class="switch-checkbox" type="checkbox" bind:checked={coupon.show_website_url}>
                      <span></span>
                    </label>
                  </label>
                </div>
                <div class="col-md-8">
                  <div class="form-group" class:has-error={isInvalid('website_url')}>
                    <label class="control-label" for="website_url" class:required={coupon.show_website_url}>
                      Website URL
                    </label>
                    <input class="form-control" id="website_url" type="url" disabled={!coupon.show_website_url}
                           bind:value={coupon.website_url}
                           onblur={() => coupon.website_url = String(coupon.website_url).trim()}
                           placeholder="Ex: http://example.com">
                    {#if isInvalid('website_url')}
                      <div class="help-block">Field is required.</div>
                    {/if}
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <label class="switch-wrapper">
                    Show Facebook URL?
                    <label class="switch">
                      <input class="switch-checkbox" type="checkbox" bind:checked={coupon.show_facebook_url}>
                      <span></span>
                    </label>
                  </label>
                </div>
                <div class="col-md-8">
                  <div class="form-group" class:has-error={isInvalid('facebook_url')}>
                    <label class="control-label" for="facebook_url" class:required={coupon.show_facebook_url}>
                      Facebook URL
                    </label>
                    <input class="form-control" id="facebook_url" type="url" disabled={!coupon.show_facebook_url}
                           bind:value={coupon.facebook_url}
                           onblur={() => coupon.facebook_url = String(coupon.facebook_url).trim()}
                           placeholder="Ex: http://example.com">
                    {#if isInvalid('facebook_url')}
                      <div class="help-block">Field is required.</div>
                    {/if}
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <label class="switch-wrapper">
                    Show Map Link?
                    <label class="switch">
                      <input class="switch-checkbox" type="checkbox" bind:checked={coupon.show_directions_url}>
                      <span></span>
                    </label>
                  </label>
                </div>
                <div class="col-md-8">
                  <div class="form-group" class:has-error={isInvalid('directions_url')}>
                    <label class="control-label" for="directions_url" class:required={coupon.show_directions_url}>
                      Location Address
                    </label>
                    <input class="form-control" id="directions_url" type="text" disabled={!coupon.show_directions_url}
                           bind:value={coupon.directions_url}
                           onblur={() => coupon.directions_url = String(coupon.directions_url).trim()}
                           placeholder="Ex: 123 Main St, Gainesville FL, 32601">
                    {#if isInvalid('directions_url')}
                      <div class="help-block">Field is required.</div>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr>
          <div onfocuscapture={setRedeemMode} class="collapsable" data-scroll-container=".phone-screen"
               data-scroll-to="#coupon-redemption">
            <input checked class="collapsable-cb" id="box4" name="collapsable-toggles" type="checkbox">
            <label class="collapsable-heading" for="box4">
              Redemption Screen
            </label>

            <div class="collapsable-content">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="redemption_type" class="control-label">Redemption type</label>
                    <select class="form-control" id="redemption_type" bind:value={coupon.redemption_type}>
                      <option value="standard">Standard</option>
                      <option value="barcode">Barcode</option>
                    </select>
                  </div>

                  <div class="form-group" hidden={coupon.redemption_type !== 'barcode'}>
                    <label for="barcode_format" class="control-label">Barcode format</label>
                    <select class="form-control" id="barcode_format" bind:value={coupon.barcode_format}>
                      <option value="qr">QR</option>
                      <option value="CODE128">Code 128</option>
                      <option value="CODE39">Code 39</option>
                      <option value="ITF14">ITF-14</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label for="redemption_action" class="control-label">Redemption instructions</label>
                    <input class="form-control" id="redemption_action" placeholder="Ex: Scan at checkout" type="text"
                           bind:value={coupon.redemption_action}
                           onblur={() => coupon.redemption_action = String(coupon.redemption_action).trim()}>
                  </div>

                  <div class="form-group" class:has-error={isInvalid('redemption_code') || hasBarcodeError}>
                    <label for="redemption_code" class="control-label required">
                      Redemption code
                      <span class="aside" hidden={!barcodeAside}>{barcodeAside}</span>
                    </label>

                    <input class="form-control" id="redemption_code" placeholder="Ex: BOGO123"
                           type={barcodeAcceptsAlpha ? 'text' : 'number'}
                           bind:value={coupon.redemption_code}
                           onblur={() => coupon.redemption_code = String(coupon.redemption_code).trim()}
                    >
                    {#if isInvalid('redemption_code')}
                      <div class="help-block">Field is required.</div>
                    {/if}
                    {#if hasBarcodeError}
                      <div class="help-block">{barcodeError}</div>
                    {/if}
                  </div>

                </div>
              </div>
            </div>
          </div>

          {#if coupon.id}
            <hr class="mt-3" />
            <div>
              <div class="h3">Inactivate?</div>
              <p>
                Making the coupon inactive will expire and remove the coupon from service.
              </p>

              <div class="input-group checkbox-button-group">
                <div class="input-group-addon">
                  <input id="coupon-active" type="checkbox" bind:checked={coupon.active} />
                </div>
                <div class="input-group-btn">
                  <label for="coupon-active" class="form-control">
                    Coupon is active
                  </label>
                </div>
              </div>

            </div>
          {/if}
        </div>

        <div class="col-md-4">
          <div class="coupon-preview-wrapper">
            <div class="coupon-phone coupon-preview" data-title="Preview">
              <div class="phone-top"></div>

              <div class="phone-screen">
                <div class="phone-screen-content">
                  <Coupon
                    {coupon}
                    {mode}
                    beginRedemption={()=>{}}
                    completeRedemption={()=>{}}
                    {barcodeRenderError}
                  />
                </div>
              </div>

              <div class="phone-bottom"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</div>
